import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import api, { fileUrl, vocRecUrl, errMsg } from "../lib/api";
import { fmtDate } from "../lib/constants";
import { fmtDur } from "../components/ActivityHub";
import { useAuth } from "../context/AuthContext";

export default function CallLog() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [status, setStatus] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [direction, setDirection] = useState("");
  const [matched, setMatched] = useState("");
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);

  const loadStatus = () => api.get("/voc/status").then((r) => setStatus(r.data)).catch(() => {});
  const load = useCallback(() => {
    api.get(`/voc/calls?page=${page}&direction=${direction}&matched=${matched}&q=${encodeURIComponent(q)}`)
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [page, direction, matched, q]);

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { load(); }, [load]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/voc/sync");
      toast.success(`Sync complete — ${r.data.synced} new call${r.data.synced === 1 ? "" : "s"} (${r.data.matched} matched to leads)`);
      loadStatus(); load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div>
          <h1 className="text-3xl font-head font-extrabold tracking-tight">Call Log</h1>
          <p className="text-muted-foreground">Calls on the Uniden numbers assigned to you, with recordings{status?.last_sync_at ? ` · last synced ${fmtDate(status.last_sync_at)}` : ""}</p>
        </div>
        {isSuper && <button onClick={doSync} disabled={syncing} data-testid="voc-sync-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-60">
          <Icons.RefreshCw size={15} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Syncing…" : "Sync Calls"}
        </button>}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Total Calls", status?.total_calls], ["Matched to Leads", status?.matched],
          ["With Recording", status?.recordings], ["Archived to Drive", status?.archived]].map(([label, val]) => (
          <div key={label} className="bg-card border border-border rounded-md p-4 shadow-sm">
            <div className="label-caps">{label}</div>
            <div className="text-xl font-head font-extrabold mt-1">{val ?? "—"}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="label-caps">Direction</label>
          <select value={direction} onChange={(e) => { setDirection(e.target.value); setPage(1); }} data-testid="voc-filter-direction"
            className="block mt-1 px-3 py-2 rounded-md border border-input bg-card text-sm">
            <option value="">All</option>
            <option value="outbound">Outgoing</option>
            <option value="inbound">Incoming</option>
          </select>
        </div>
        <div>
          <label className="label-caps">Lead Match</label>
          <select value={matched} onChange={(e) => { setMatched(e.target.value); setPage(1); }} data-testid="voc-filter-matched"
            className="block mt-1 px-3 py-2 rounded-md border border-input bg-card text-sm">
            <option value="">All</option>
            <option value="yes">Matched</option>
            <option value="no">Unmatched</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px] max-w-xs">
          <label className="label-caps">Search</label>
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Number, agent or lead name…"
            data-testid="voc-search" className="block w-full mt-1 px-3 py-2 rounded-md border border-input bg-card text-sm" />
        </div>
        <div className="ml-auto text-sm text-muted-foreground pb-2">{data.total} calls</div>
      </div>

      <div className="bg-card border border-border rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="voc-calls-table">
          <thead><tr className="text-left label-caps border-b border-border">
            <th className="p-3">Date</th><th>Direction</th><th>Agent</th><th>Customer #</th><th>Lead</th><th>Duration</th><th className="min-w-[260px]">Recording</th></tr></thead>
          <tbody>
            {data.items.map((c) => {
              const out = c.direction === "outbound";
              const custNum = out ? c.destination_number : c.callerid;
              return (
                <tr key={c.id} className="border-b border-border/50" data-testid="voc-call-row">
                  <td className="p-3 whitespace-nowrap">{c.date}</td>
                  <td>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded ${out ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
                      {out ? <Icons.PhoneOutgoing size={12} /> : <Icons.PhoneIncoming size={12} />}
                      {out ? "Outgoing" : "Incoming"}
                    </span>
                  </td>
                  <td className="whitespace-nowrap">{c.callerid_name || "—"}{c.extension ? <span className="text-muted-foreground text-xs"> · ext {c.extension}</span> : null}</td>
                  <td className="whitespace-nowrap font-medium">{custNum || "—"}</td>
                  <td>
                    {c.lead_id ? (
                      <Link to={`/app/leads/${c.lead_id}`} className="text-primary underline font-medium" data-testid="voc-lead-link">{c.lead_name || "View lead"}</Link>
                    ) : <span className="text-muted-foreground text-xs">Unmatched</span>}
                  </td>
                  <td className="whitespace-nowrap">{c.billed_seconds ? fmtDur(c.billed_seconds) : "—"}</td>
                  <td className="py-2 pr-3">
                    {c.has_recording ? (
                      <div className="flex items-center gap-2">
                        <audio controls preload="none" data-testid="voc-recording-audio"
                          src={c.recording_file_id ? fileUrl(c.recording_file_id) : vocRecUrl(c.voc_id)}
                          className="h-9 max-w-[220px]" />
                        <a href={c.recording_file_id ? fileUrl(c.recording_file_id) : vocRecUrl(c.voc_id)} target="_blank" rel="noreferrer"
                          title="Download recording" data-testid="voc-recording-download"
                          className="text-primary hover:opacity-80"><Icons.Download size={16} /></a>
                        {c.recording_file_id ? <Icons.CloudUpload size={14} className="text-emerald-600" title="Archived to Drive" /> : null}
                      </div>
                    ) : <span className="text-muted-foreground text-xs">{c.billed_seconds ? "No recording" : "Not answered"}</span>}
                  </td>
                </tr>
              );
            })}
            {data.items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No calls found yet for your assigned numbers.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="voc-prev-page"
          className="px-3 py-1.5 rounded-md border border-border text-sm font-medium disabled:opacity-40">Previous</button>
        <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages}</span>
        <button onClick={() => setPage((p) => Math.min(data.pages, p + 1))} disabled={page >= data.pages} data-testid="voc-next-page"
          className="px-3 py-1.5 rounded-md border border-border text-sm font-medium disabled:opacity-40">Next</button>
      </div>
    </div>
  );
}
